/**
 * @author Dmitriy Lebedev
 */

/* eslint-disable no-restricted-syntax */

import { ComponentType } from 'react';
import { makeAutoObservable } from 'mobx';
import { debounce } from 'lodash';
import { ensureNotNil } from '@ui-kit/lib/asserts';
import { safeImport } from 'shared/lib/safe.import';
import { DIALOG_PRELOAD_ENABLED, isDevEnv } from 'shared/config';
import type { ModalDialog } from './modal.dialog';
import { RootStore } from './root.store';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface IDialogs {}

export type IDialogName = keyof IDialogs;

export type IDialogComponentLoader<TDialog extends keyof IDialogs> = () => Promise<{ readonly default: ComponentType<{ dialog: IDialogs[TDialog] }> }>;

export type IDialogComponentLoaders = {
  [TDialog in keyof IDialogs]: IDialogComponentLoader<TDialog>;
};

export type IDialogsPick<TDialogName extends IDialogName> = Pick<IDialogs, TDialogName>;

export type IDialogGetConfig<TDialogName extends IDialogName> = IDialogs[TDialogName] extends ModalDialog<infer TDialogConfig>
  ? Omit<TDialogConfig, 'dialog' | 'scope'>
  : never;

export type IDialogGetReturn<TDialogName extends IDialogName> = IDialogs[TDialogName] extends ModalDialog<any, infer TDialogReturn> ? TDialogReturn : never;

export type IDialogsConfig = {
  [TDialog in keyof IDialogs]: IDialogGetConfig<TDialog>;
};

export type IDialogsReturn = {
  [TDialog in keyof IDialogs]: IDialogGetReturn<TDialog>;
};

/**
 * @description used to call dialogs from models, helps avoid dependency cycles
 */
export class DialogsStore {
  public static instance() {
    return RootStore.instance.get('dialogsStore');
  }

  constructor() {
    makeAutoObservable(this, undefined, { autoBind: true });
  }

  public static getIsValidDialogName(name: IDialogName) {
    return this.componentLoaders.has(name);
  }

  private static componentLoaders = new Map<IDialogName, IDialogComponentLoader<any>>();

  private static componentLoading = new Set<IDialogName>();

  public static setComponentLoaders(componentLoaders: IDialogComponentLoaders) {
    this.componentLoaders.clear();

    Object.keys(componentLoaders).forEach((name) => {
      this.componentLoaders.set(name as IDialogName, () => safeImport(componentLoaders[name]));
    });
  }

  public static async loadComponent(name: IDialogName) {
    try {
      this.componentLoading.add(name);

      const loader = ensureNotNil(this.componentLoaders.get(name), `${name} Dialog Component Loader`);

      return await loader();
    } finally {
      this.componentLoading.delete(name);
    }
  }

  public static getIsComponentLoading(name: IDialogName) {
    return this.componentLoading.has(name);
  }

  private static toPreload = new Set<IDialogName>();

  private static preloaded = new Set<IDialogName>();

  public static preload(names: IDialogName[]) {
    if (!DIALOG_PRELOAD_ENABLED || isDevEnv) {
      return;
    }

    names.forEach((name) => !this.preloaded.has(name) && this.toPreload.add(name));

    this.preloadHandler();
  }

  private static preloadHandler = debounce(async () => {
    for (const name of [...this.toPreload]) {
      if (this.preloaded.has(name)) {
        this.toPreload.delete(name);
        return;
      }

      // eslint-disable-next-line no-await-in-loop
      await this.componentLoaders.get(name)?.();

      this.preloaded.add(name);
      this.toPreload.delete(name);
    }
  }, 3000);
}

declare module 'shared/model/root.store' {
  interface IStores {
    dialogsStore: DialogsStore;
  }
}

RootStore.setSetupFn('dialogsStore', () => new DialogsStore());
